'use client'

import { MathManager } from '@payments-checkout/shared/helpers/managers/mathManager';
import { usePaymentMethod } from '@payments-checkout/shared/providers';
import { Divider, Text, type TextColor } from '@stone-payments/jade';
import { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useFormTypedValues } from '../../app/providers/formValuesProvider.provider';
import { usePageSharedData } from '../../hooks/usePageSharedData';
import { SummaryProps } from '../../types/sumaryCard.types';
import { SummaryItem, SummaryTable } from './style';

type ItemsProps = {
  title: string;
  description: {
      color: TextColor | undefined;
      weight: "medium" | "semibold" | "bold" | "regular" | undefined;
      content: string;
  };
  surcharges?: string | null;
}[]

const buildSummaryDetail = (
  items: ItemsProps,
  explanationText: string | null,
  surchargesText: string | null,
  deliveryQuote?: string | null,
  isDeliveryEnabled?: boolean,
  discountValue?: string | null,
  incrementValue?: string | null
) => {
  const result = [...items];

  if (isDeliveryEnabled && deliveryQuote) {
    result.push({
      title: 'delivery',
      description: {
        color: 'medium',
        content: deliveryQuote,
        weight: 'semibold'
      }
    });
  }

  if (discountValue) {
    result.push({
      title: 'discount',
      description: {
        color: 'positive',
        content: `${discountValue}`,
        weight: 'semibold'
      }
    });
  }

  if (incrementValue) {
    result.push({
      title: 'increment',
      description: {
        color: 'medium',
        content: `+ ${incrementValue}`,
        weight: 'semibold'
      }
    });
  }

  if (explanationText) {
    result.push({
      title: 'installments',
      description: {
        color: 'medium',
        content: explanationText,
        weight: 'semibold'
      },
      surcharges: surchargesText
    });
  }

  return result;
}

type SummaryDetailProps = Pick<SummaryProps, 'items' | 'total'> & {
  removeBorderRadiusTop?: boolean;
  totalLabel?: string;
};

export function SummaryDetail({ items, total, removeBorderRadiusTop, totalLabel }: SummaryDetailProps) {
  const { formatMessage } = useIntl()
  const { deliveryQuote, installmentsStoneDelivery } = useFormTypedValues();
  const {installmentsExplanationText, paymentMethod, selectedDigitalWallet, surchargesText, installments, brand} = usePaymentMethod()
  const { isDeliveryEnabled } = usePageSharedData();
  const deliveryCost = deliveryQuote ? MathManager.formatCurrency({ value: deliveryQuote.cost / 100 }) : null;

  const stoneDeliveryExplanationText = useMemo(() => {
    if (!installmentsStoneDelivery || !installments) return null;

    const matchingBrandPlan =
      installmentsStoneDelivery.find(
        (entry) => entry.brand.toLowerCase() === (brand || '').toLowerCase()
      ) ??
      installmentsStoneDelivery.find(
        (entry) => entry.brand.toLowerCase() === 'default'
      );

    if (!matchingBrandPlan) return null;

    const found = matchingBrandPlan.installments.find((installment) => installment.number === installments);
    if (!found) return null;

    return `${found.number}x de ${MathManager.formatCurrency({ value: (found.total / found.number / 100).toFixed(2) })}`;
  }, [installments, installmentsStoneDelivery, brand]);

  const explanationText = useMemo(() => {
    if (isDeliveryEnabled) {
      return stoneDeliveryExplanationText;
    }
    return installmentsExplanationText;
  }, [isDeliveryEnabled, stoneDeliveryExplanationText, installmentsExplanationText]);

  const discountValue = useMemo(() => {
    return total.discountValue || null;
  }, [total.discountValue]);

  const incrementValue = useMemo(() => {
    return total.incrementValue || null;
  }, [total.incrementValue]);

  const completeItems = buildSummaryDetail(
    items,
    explanationText,
    surchargesText,
    deliveryCost,
    isDeliveryEnabled,
    discountValue,
    incrementValue
  )

  const effectiveTotalAmount = useMemo(() => {
    if (!isDeliveryEnabled) return total.formattedAmount;

    if (installments && installmentsStoneDelivery) {
      const matchingBrandPlan =
        installmentsStoneDelivery.find(
          (entry) => entry.brand.toLowerCase() === (brand || '').toLowerCase()
        ) ??
        installmentsStoneDelivery.find(
          (entry) => entry.brand.toLowerCase() === 'default'
        );
      const found = matchingBrandPlan?.installments.find((i) => i.number === installments);
      if (found) {
        return MathManager.formatCurrency({ value: (found.total / 100).toFixed(2) });
      }
    }

    if (deliveryQuote) {
      return MathManager.formatCurrency({ value: ((total.amount + deliveryQuote.cost) / 100).toFixed(2) });
    }

    return total.formattedAmount;
  }, [isDeliveryEnabled, installments, installmentsStoneDelivery, brand, deliveryQuote, total, paymentMethod]);

  const paymentItems = useMemo(() => {
    const removeInstallments = paymentMethod && ['pix', 'boleto'].includes(paymentMethod)
    if (removeInstallments) {
      return completeItems.filter(value => value.title !== "installments");
    }
    return completeItems;
  }, [completeItems, paymentMethod, installments, selectedDigitalWallet])

  return (
    <SummaryTable removeBorderRadiusTop={removeBorderRadiusTop}>
      {paymentItems.map(({ title, description, surcharges }) => (
        <SummaryItem key={title}>
          <Text color="medium">{formatMessage({ id: `info.costs.${title}` })}</Text>
          <Text color={description.color} weight={description.weight}>
            {description.content} {surcharges ? formatMessage({ id: `${surcharges}` }) : null}
          </Text>
        </SummaryItem>
      ))}
      <Divider />
      <div className="flex justify-between items-center pt-jade-100">
        <Text variant="text-large" weight="bold">
          {totalLabel ?? formatMessage({ id: 'info.costs.total' })}
        </Text>
        <Text variant="text-large" weight="bold">
          {effectiveTotalAmount}
        </Text>
      </div>
    </SummaryTable>
  );
}
